import { useLocation } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { KeyboardArrowDown } from '@mui/icons-material';
import { HydrationSafeSlide } from 'ui-component/HydrationSafeReveal';
import './style.css';

const PageTitle = ({
    page,
    columnOne,
    columnTwo,
    textBoxFlex,
    imageBoxFlex,
    customMargin,
    columnOneColor,
    columnTwoColor,
    fontSize,
    textAlignment,
    photo,
    photoMobile,
    disableMediaReveal,
    customImageSize,
    customImageMargin,
    customImageMobileViewMaxWidth,
    heroIntrinsicSize,
    reverse,
    video,
    children
}) => {
    const location = useLocation();
    const theme = useTheme();
    const photoMobileMedia = photoMobile ? theme.breakpoints.down('sm').replace(/^@media\s*/, '') : '';

    const heroImgCommon = {
        alt: 'Page cover photo',
        // Async decode avoids blocking the main thread before first paint.
        decoding: 'async',
        loading: 'eager',
        fetchpriority: 'high',
        importance: 'high',
        ...(heroIntrinsicSize && {
            width: heroIntrinsicSize.width,
            height: heroIntrinsicSize.height
        })
    };

    const heroMediaSx = {
        '& img, & picture img': {
            width: customImageSize ? customImageSize : '100%',
            height: 'auto',
            display: 'block',
            ...(heroIntrinsicSize && { aspectRatio: `${heroIntrinsicSize.width} / ${heroIntrinsicSize.height}` }),
            ...(customImageMargin
                ? { margin: customImageMargin }
                : {
                      margin: { xs: '0 0 60px 0', md: '0' }
                  }),
            ...(customImageMobileViewMaxWidth
                ? { maxWidth: { xs: customImageMobileViewMaxWidth, md: 'unset' } }
                : { maxWidth: { xs: '400px', md: 'unset' } })
        }
    };

    const renderHeroMedia = () => {
        if (photoMobile) {
            return (
                <picture fetchpriority="high" loading="eager">
                    <source media={photoMobileMedia} srcSet={photoMobile} />
                    <img {...heroImgCommon} src={photo} alt="Page cover photo" />
                </picture>
            );
        }
        return <img {...heroImgCommon} src={photo} alt="Page cover photo" />;
    };

    const getArrowColor = () => {
        const homePage = ['/'];
        const mediaServiceRelatedPage = [
            '/media-service',
            '/data-driven-programmatic',
            '/premium-placement',
            '/campaign-management-service',
            '/china-media-solutions',
            '/ai-creation'
        ];
        const dataServiceRelatedPage = ['/data-service', '/external-data', '/insight-report', '/etl-project'];

        let color = 'transparent';
        if (typeof window !== 'undefined' && location.pathname) {
            if (homePage.includes(location.pathname)) {
                color = '#E15C12';
            } else if (mediaServiceRelatedPage.includes(location.pathname)) {
                color = '#4D92BB';
            } else if (dataServiceRelatedPage.includes(location.pathname)) {
                color = '#7B6CF9';
            }
        }

        return color;
    };

    return (
        <Box id={page} className="page-title-container">
            <Box className={reverse ? 'page-title-content-reverse' : 'page-title-content'}>
                <Box className="page-title-text-column" style={{ '--page-title-text-flex': textBoxFlex ?? '1' }}>
                    <Box className="page-title-text-box">
                        <Typography variant="h1" component="h1" className="page-title-text">
                            <span className={`page-title-text-row page-title-text-${columnOneColor}`}>{columnOne}</span>
                            {columnTwo && <span className={`page-title-text-row page-title-text-${columnTwoColor}`}>{columnTwo}</span>}
                        </Typography>
                    </Box>
                    {children}
                </Box>
                {photo && (
                    <Box className="page-title-media-column" style={{ '--page-title-media-flex': imageBoxFlex ?? '1' }} sx={heroMediaSx}>
                        {disableMediaReveal ? (
                            renderHeroMedia()
                        ) : (
                            <HydrationSafeSlide direction={reverse ? 'left' : 'right'} fraction={0.8} triggerOnce>
                                {renderHeroMedia()}
                            </HydrationSafeSlide>
                        )}
                    </Box>
                )}
                {video && (
                    <Box className="page-title-media-column" style={{ '--page-title-media-flex': imageBoxFlex ?? '1' }}>
                        <video
                            autoPlay={typeof window !== 'undefined'}
                            loop
                            muted
                            playsInline
                            fetchPriority="high"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                            }}
                        >
                            <source src={video} type="video/webm" />
                            Your browser does not support the video tag.
                        </video>
                    </Box>
                )}
            </Box>
            <Box
                sx={(t) => ({
                    display: 'flex',
                    alignItems: 'center',
                    position: 'absolute',
                    bottom: '10vh',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    [t.breakpoints.down('md')]: {
                        position: 'unset',
                        transform: 'unset',
                        justifyContent: 'center',
                        margin: '40px 0'
                    }
                })}
            >
                <Typography
                    sx={(t) => ({
                        color: '#4D4D4D',
                        fontSize: '20px',
                        fontWeight: 500,
                        [t.breakpoints.down('md')]: {
                            fontSize: '14px'
                        }
                    })}
                >
                    scroll down
                </Typography>
                <KeyboardArrowDown sx={{ color: `${getArrowColor()}`, alignSelf: 'center' }} />
            </Box>
        </Box>
    );
};

export default PageTitle;
